import { useEffect } from 'react';

const LAST_INSET_KEY = 'volt.lastKeyboardInset';
let lastKeyboardInset: number | null = null;

function rememberKeyboardInset(inset: number): void {
  if (inset < 100) return;
  lastKeyboardInset = inset;
  try { window.localStorage.setItem(LAST_INSET_KEY, String(Math.round(inset))); } catch { /* ignore */ }
}

function readRememberedKeyboardInset(): number | null {
  if (lastKeyboardInset !== null) return lastKeyboardInset;
  try {
    const raw = Number(window.localStorage.getItem(LAST_INSET_KEY));
    return Number.isFinite(raw) && raw >= 100 ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Best guess of the keyboard height before the browser reports it: the last
 * measured height, or ~38% of the screen. Used to start moving the composer
 * together with the keyboard instead of after it.
 */
export function predictKeyboardInset(lastKnown: number | null, layoutViewportHeight: number): number {
  const guess = lastKnown && lastKnown >= 100 ? lastKnown : Math.round(layoutViewportHeight * 0.38);
  return Math.min(guess, Math.round(layoutViewportHeight * 0.6));
}

export function getKeyboardInset(layoutViewportHeight: number, viewport: VisualViewport): number {
  return Math.max(0, layoutViewportHeight - viewport.offsetTop - viewport.height);
}

interface KeyboardInsetViewport {
  offsetTop: number;
  height: number;
  addEventListener: (type: 'resize' | 'scroll', listener: () => void, options?: { passive: true }) => void;
  removeEventListener: (type: 'resize' | 'scroll', listener: () => void) => void;
}

interface AttachKeyboardInsetOptions {
  viewport: KeyboardInsetViewport;
  getLayoutViewportHeight: () => number;
  getScrollY: () => number;
  scrollToTop: () => void;
  setStyleProperty: (name: string, value: string) => void;
  removeStyleProperty: (name: string) => void;
  requestFrame: (callback: () => void) => number;
  cancelFrame: (id: number) => void;
}

export function attachKeyboardInsetViewport(options: AttachKeyboardInsetOptions): () => void {
  const {
    viewport,
    getLayoutViewportHeight,
    getScrollY,
    scrollToTop,
    setStyleProperty,
    removeStyleProperty,
    requestFrame,
    cancelFrame,
  } = options;
  let rafId = 0;

  function apply() {
    if (getScrollY() !== 0) scrollToTop();
    const inset = getKeyboardInset(getLayoutViewportHeight(), viewport as VisualViewport);
    rememberKeyboardInset(inset);
    setStyleProperty('--keyboard-inset', `${inset}px`);
    setStyleProperty('--visual-viewport-height', `${viewport.height}px`);
    setStyleProperty('--visual-viewport-offset-top', `${viewport.offsetTop}px`);
  }

  function update() {
    cancelFrame(rafId);
    rafId = requestFrame(apply);
  }

  viewport.addEventListener('resize', update, { passive: true });
  viewport.addEventListener('scroll', update, { passive: true });
  apply();

  return () => {
    cancelFrame(rafId);
    viewport.removeEventListener('resize', update);
    viewport.removeEventListener('scroll', update);
    removeStyleProperty('--keyboard-inset');
    removeStyleProperty('--visual-viewport-height');
    removeStyleProperty('--visual-viewport-offset-top');
  };
}

function isTextEntry(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.tagName !== 'string') return false;
  if (el.isContentEditable) return true;
  if (el.tagName === 'TEXTAREA') return true;
  if (el.tagName !== 'INPUT') return false;
  return !['button', 'checkbox', 'radio', 'range', 'file', 'submit', 'reset', 'image', 'color'].includes(
    (el as HTMLInputElement).type,
  );
}

/**
 * iOS reports the keyboard only after it has finished appearing, so the app
 * used to resize *after* the keyboard and the composer visibly jumped.
 *
 * On focus of a text field we start the (single, instant) layout change right
 * away with a predicted keyboard height. The composer then glides from its old
 * position with a transform-only animation (FLIP) — cheap, compositor-driven,
 * and no per-frame layout like animating `height` would cause. When the real
 * viewport size arrives the difference is animated the same way.
 */
function flipComposers(deltaPx: number): void {
  if (Math.abs(deltaPx) < 2) return;
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  document.querySelectorAll<HTMLElement>('.composer, .direct-chat-composer').forEach((el) => {
    if (typeof el.animate !== 'function') return;
    el.animate(
      [{ transform: `translateY(${deltaPx}px)` }, { transform: 'translateY(0)' }],
      { duration: 260, easing: 'cubic-bezier(0.22, 0.8, 0.3, 1)' },
    );
  });
}

function attachKeyboardPrediction(vv: VisualViewport): () => void {
  if (typeof window.matchMedia !== 'function' || !window.matchMedia('(pointer: coarse)').matches) {
    return () => undefined;
  }
  const root = document.documentElement;
  let revertTimer: number | undefined;
  let blurTimer: number | undefined;
  let applied = getKeyboardInset(window.innerHeight, vv);

  const setInset = (inset: number) => {
    flipComposers(inset - applied);
    applied = inset;
    root.style.setProperty('--keyboard-inset', `${inset}px`);
    root.style.setProperty('--visual-viewport-height', `${window.innerHeight - inset}px`);
    root.style.setProperty('--visual-viewport-offset-top', '0px');
    // Let layout-dependent UI (e.g. the page-switcher position) re-measure.
    requestAnimationFrame(() => window.dispatchEvent(new Event('volt-layout')));
  };

  const onFocusIn = (event: FocusEvent) => {
    window.clearTimeout(blurTimer);
    if (!isTextEntry(event.target)) return;
    // Keyboard already open (moving between fields): nothing to predict.
    if (getKeyboardInset(window.innerHeight, vv) > 100 || applied > 100) return;
    setInset(predictKeyboardInset(readRememberedKeyboardInset(), window.innerHeight));
    window.clearTimeout(revertTimer);
    // No keyboard showed up (hardware keyboard, desktop mode): undo the guess.
    revertTimer = window.setTimeout(() => {
      if (getKeyboardInset(window.innerHeight, vv) < 100) setInset(0);
    }, 900);
  };
  const onFocusOut = () => {
    window.clearTimeout(blurTimer);
    blurTimer = window.setTimeout(() => {
      if (isTextEntry(document.activeElement)) return;
      if (applied < 100) return;
      setInset(0);
    }, 60);
  };
  // Real viewport values: animate only the difference from what we applied.
  const onViewport = () => {
    const real = getKeyboardInset(window.innerHeight, vv);
    flipComposers(real - applied);
    applied = real;
  };

  document.addEventListener('focusin', onFocusIn);
  document.addEventListener('focusout', onFocusOut);
  vv.addEventListener('resize', onViewport, { passive: true } as AddEventListenerOptions);
  return () => {
    document.removeEventListener('focusin', onFocusIn);
    document.removeEventListener('focusout', onFocusOut);
    vv.removeEventListener('resize', onViewport);
    window.clearTimeout(revertTimer);
    window.clearTimeout(blurTimer);
  };
}

/**
 * Track the on-screen keyboard height via the Visual Viewport API and expose it
 * as the `--keyboard-inset` CSS custom property on `documentElement`.
 *
 * This lets the composer translate up in sync with the keyboard so the layout
 * never jumps in two separate steps (the "double jump" seen on iOS Safari when
 * the browser first scrolls to the focused element and then recalculates the
 * layout).
 *
 * Android Chrome with `interactive-widget=resizes-content` in the viewport meta
 * already resizes the visual viewport, so the same hook works on both platforms.
 */
export function useKeyboardInset(): void {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const detachPrediction = attachKeyboardPrediction(vv);
    const detachViewport = attachKeyboardInsetViewport({
      viewport: vv,
      getLayoutViewportHeight: () => window.innerHeight,
      getScrollY: () => window.scrollY,
      scrollToTop: () => window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior }),
      setStyleProperty: (name, value) => document.documentElement.style.setProperty(name, value),
      removeStyleProperty: (name) => document.documentElement.style.removeProperty(name),
      requestFrame: (callback) => requestAnimationFrame(callback),
      cancelFrame: (id) => cancelAnimationFrame(id),
    });
    return () => { detachPrediction(); detachViewport(); };
  }, []);
}
